import { useState, useEffect, useRef, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MapPin, Shield, Navigation, Settings, RefreshCw } from "lucide-react";
import { isIOS } from "@/lib/capacitor";
import {
  checkLocationPermission,
  requestLocationPermission,
  openNativeAppSettings,
  onAppResume,
  type LocationPermissionLevel,
} from "@/lib/nativeLocation";

interface NativeLocationPermissionProps {
  open: boolean;
  onComplete: () => void;
}

/**
 * Native-only location permission dialog.
 * 
 * Key guarantees:
 * - NEVER freezes the UI
 * - Permission check runs exactly once per open (guarded by ref)
 * - Permission request is guarded against concurrent calls (in nativeLocation.ts)
 * - After Open Settings, auto re-checks on app resume
 * - Always provides a "Skip" escape hatch
 */
export const NativeLocationPermission = ({
  open,
  onComplete,
}: NativeLocationPermissionProps) => {
  const [requesting, setRequesting] = useState(false);
  const [permissionLevel, setPermissionLevel] = useState<LocationPermissionLevel>("unknown");
  const [showSettingsPrompt, setShowSettingsPrompt] = useState(false);
  const [showTimeoutRecovery, setShowTimeoutRecovery] = useState(false);
  const [preciseLocationOn, setPreciseLocationOn] = useState(true);

  const isNative = Capacitor.isNativePlatform();
  const hasCheckedRef = useRef(false);
  const cleanupResumeRef = useRef<(() => void) | null>(null);

  // ─── Initial check (once per open) ──────────────────────────────────────────
  useEffect(() => {
    if (!isNative || !open) {
      hasCheckedRef.current = false;
      return;
    }
    if (hasCheckedRef.current) return;
    hasCheckedRef.current = true;

    let cancelled = false;

    (async () => {
      const info = await checkLocationPermission();
      if (cancelled) return;

      setPermissionLevel(info.permission);
      setPreciseLocationOn(info.preciseLocation);

      if (info.permission === "granted") {
        console.log("[NativeLocationPermission] Already granted, completing");
        onComplete();
      }
    })();

    return () => { cancelled = true; };
  }, [isNative, open, onComplete]);

  // ─── Resume listener (active only when showing settings prompt) ─────────────
  useEffect(() => {
    if (!isNative || !open || !showSettingsPrompt) return;

    const cleanup = onAppResume(async () => {
      console.log("[NativeLocationPermission] App resumed, re-checking");
      const info = await checkLocationPermission();
      setPermissionLevel(info.permission);
      setPreciseLocationOn(info.preciseLocation);

      if (info.permission === "granted") {
        setShowSettingsPrompt(false);
        onComplete();
      }
    });

    cleanupResumeRef.current = cleanup;

    return () => {
      cleanup();
      cleanupResumeRef.current = null;
    };
  }, [isNative, open, showSettingsPrompt, onComplete]);

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleRequestPermission = useCallback(async () => {
    if (requesting) return;
    setRequesting(true);
    setShowTimeoutRecovery(false);

    try {
      const info = await requestLocationPermission();

      // Check if the request timed out (custom flag from nativeLocation.ts)
      if ((info as any)._timedOut) {
        console.warn("[NativeLocationPermission] Request timed out, showing recovery");
        setPermissionLevel(info.permission);
        setPreciseLocationOn(info.preciseLocation);
        setShowTimeoutRecovery(true);
        return;
      }

      setPermissionLevel(info.permission);
      setPreciseLocationOn(info.preciseLocation);

      if (info.permission === "granted") {
        onComplete();
      } else if (info.permission === "denied") {
        // OS won't prompt again — must go to Settings
        setShowSettingsPrompt(true);
      } else if (info.permission === "limited") {
        // iOS "When In Use" — good enough, proceed
        onComplete();
      } else {
        // prompt or unknown — complete to not block
        onComplete();
      }
    } catch (error) {
      console.error("[NativeLocationPermission] Request error:", error);
      onComplete();
    } finally {
      setRequesting(false);
    }
  }, [requesting, onComplete]);

  const handleOpenSettings = useCallback(async () => {
    console.log("[NativeLocationPermission] Opening settings");
    
    // Before sending to settings, ensure we've requested permission at least once
    // so iOS registers the Location category in Settings
    const info = await checkLocationPermission();
    if (info.permission === "prompt") {
      // Request first so iOS knows we use location
      await requestLocationPermission();
    }
    
    openNativeAppSettings();
    // Don't call onComplete — wait for resume listener
    setShowTimeoutRecovery(false);
    setShowSettingsPrompt(true);
  }, []);

  const handleTryAgain = useCallback(async () => {
    setShowTimeoutRecovery(false);
    const info = await checkLocationPermission();
    setPermissionLevel(info.permission);
    setPreciseLocationOn(info.preciseLocation);

    if (info.permission === "granted") {
      setShowSettingsPrompt(false);
      onComplete();
    } else if (info.canPromptAgain) {
      setShowSettingsPrompt(false);
      handleRequestPermission();
    }
    // If still denied, stay on settings prompt
  }, [onComplete, handleRequestPermission]);

  const handleSkip = useCallback(() => {
    console.log("[NativeLocationPermission] Skipped");
    // Save dismiss time so we don't prompt again for 24 hours
    localStorage.setItem("location_permission_dismissed_at", Date.now().toString());
    setShowSettingsPrompt(false);
    setShowTimeoutRecovery(false);
    onComplete();
  }, [onComplete]);

  // ─── Render guards ──────────────────────────────────────────────────────────

  if (!isNative || permissionLevel === "granted") return null;

  // ─── Timeout recovery prompt ───────────────────────────────────────────────

  if (showTimeoutRecovery) {
    return (
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-md mx-4"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center">
              <RefreshCw className="w-8 h-8 text-warning" />
            </div>
            <DialogTitle className="text-xl">
              Permission Request Didn't Complete
            </DialogTitle>
            <DialogDescription className="text-center pt-2">
              The location permission request didn't respond in time. You can try again, or enable location access manually in Settings.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button onClick={handleRequestPermission} disabled={requesting} className="w-full" size="lg">
              {requesting ? <span role="status" aria-live="polite">Requesting…</span> : "Try Again"}
            </Button>
            <Button variant="outline" onClick={handleOpenSettings} className="w-full" size="default">
              <Settings className="w-4 h-4 mr-2" />
              Open Settings
            </Button>
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="w-full text-muted-foreground"
              size="sm"
            >
              Skip for now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // ─── Settings / denied prompt ───────────────────────────────────────────────

  if (showSettingsPrompt) {
    const isDenied = permissionLevel === "denied";
    const needsPrecise = !preciseLocationOn && isIOS;

    return (
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-md mx-4"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center">
              <Settings className="w-8 h-8 text-warning" />
            </div>
            <DialogTitle className="text-xl">
              {isDenied
                ? "Location Access Required"
                : needsPrecise
                ? "Enable Precise Location"
                : "Enable Background Location"}
            </DialogTitle>
            <DialogDescription className="text-center pt-2">
              {isDenied
                ? "Location permission was denied. Please enable it in Settings so we can track your deliveries."
                : needsPrecise
                ? "Precise location is off. Please enable it in Settings for accurate tracking."
                : isIOS
                ? 'To track deliveries while the app is closed, change location access to "Always" in Settings.'
                : "To track deliveries in the background, update location access in Settings."}
            </DialogDescription>
          </DialogHeader>

          <div className="bg-muted/50 rounded-2xl p-4 text-sm">
            <p className="font-medium text-foreground mb-2">How to enable:</p>
            <ol className="list-decimal list-inside space-y-1.5 text-muted-foreground">
              {isIOS ? (
                needsPrecise ? (
                  <>
                    <li>Tap <span className="font-semibold text-foreground">"Open Settings"</span> below</li>
                    <li>Select "Location"</li>
                    <li>Toggle <span className="font-semibold text-foreground">"Precise Location"</span> on</li>
                  </>
                ) : (
                  <>
                    <li>Tap <span className="font-semibold text-foreground">"Open Settings"</span> below</li>
                    <li>Select "Location"</li>
                    <li>Choose <span className="font-semibold text-foreground">"Always"</span></li>
                  </>
                )
              ) : (
                <>
                  <li>Tap <span className="font-semibold text-foreground">"Open Settings"</span> below</li>
                  <li>Select "Permissions" → "Location"</li>
                  <li>Choose <span className="font-semibold text-foreground">"Allow all the time"</span></li>
                </>
              )}
            </ol>
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button onClick={handleOpenSettings} className="w-full" size="lg">
              <Settings className="w-4 h-4 mr-2" />
              Open Settings
            </Button>
            <Button
              variant="outline"
              onClick={handleTryAgain}
              className="w-full"
              size="default"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Check Again
            </Button>
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="w-full text-muted-foreground"
              size="sm"
            >
              Skip for now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // ─── Initial permission request prompt ──────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md mx-4"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
            <MapPin className="w-8 h-8 text-primary" />
          </div>
          <DialogTitle className="text-xl">
            Allow TransportMate to access your location?
          </DialogTitle>
          <DialogDescription className="text-center pt-2">
            Location access enables live tracking for deliveries so customers can
            see your progress in real-time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-primary/10">
              <Navigation className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-sm">Real-time Tracking</p>
              <p className="text-xs text-muted-foreground">
                Customers can track your delivery progress live
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-primary/10">
              <Shield className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-sm">Background Tracking</p>
              <p className="text-xs text-muted-foreground">
                Continues even when the app is minimized or phone is locked
              </p>
            </div>
          </div>
        </div>

        <div className="bg-muted/50 rounded-2xl p-3 text-xs text-muted-foreground">
          <p className="font-medium text-foreground mb-1">Tip:</p>
          <p>
            When prompted, select{" "}
            <span className="font-semibold text-foreground">
              {isIOS ? '"Allow While Using App"' : '"Allow all the time"'}
            </span>
            {isIOS && ". You can enable \"Always\" later in Settings for best background tracking."}
          </p>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            onClick={handleRequestPermission}
            disabled={requesting}
            className="w-full"
            size="lg"
          >
            {requesting ? <span role="status" aria-live="polite">Requesting…</span> : "Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
